import { useTable } from 'react-table'
import { Link } from '@remix-run/react'
import clsx from 'clsx'
import {
	ArrowDownIcon,
	ArrowUpIcon,
	OpenInNewIcon,
	VerifiedIcon,
	WebsiteIcon,
} from '../icons'
import { useMemo, useRef } from 'react'
import { TypographyLabel, TypographyParagraph } from '../typography'
import {
	formatChainName,
	getCollectionProfileURL,
	getContractAddressExplorerUrl,
	getMetadataProfileURL,
	mapPlatform,
	truncateStringInMiddle,
} from '#app/utils/misc.tsx'
import Image from '../content/Image'
import InternalLink from '../common/internalLink'

const CollectionRow = ({ value, column, row }) => {
	return (
		<div className="flex items-center p-2">
			<Image
				className="h-[56px] w-[56px] rounded object-cover"
				containerClassName="min-w-[56px]"
				imageUrl={
					row.original.properties.web2URL
						? row.original.properties.web2URL
						: row.original.properties.imageUrl
				}
				imageAlt={row.original.properties.name}
			/>
			<div className="truncate break-words pl-4 pr-2">
				<InternalLink
					to={getCollectionProfileURL(row.original)}
					prefetch="none"
				>
					<TypographyLabel
						size="medium"
						isBold={true}
						additionalClasses="hover:underline"
					>
						{row.original.properties.name}
					</TypographyLabel>
				</InternalLink>
			</div>
			<VerifiedIcon size="medium" />
		</div>
	)
}

const PlatformRow = ({ value, column, row }) => {
	return (
		<div className="flex items-center justify-center gap-x-4 py-0 pl-0 pr-2">
			<TypographyParagraph size="small">
				{mapPlatform(row.original)}
			</TypographyParagraph>
		</div>
	)
}

const LinksRow = ({ value, column, row }) => {
	return (
		<div className="flex items-center justify-center gap-x-4 py-0 pl-0 pr-2">
			{row.original.properties.url ? (
				<Link to={row.original.properties.url} target="_blank">
					<WebsiteIcon className="h-4 w-4" aria-hidden="true" />
				</Link>
			) : (
				''
			)}
		</div>
	)
}

const SignatureRow = ({ value, column, row }) => {
	return (
		<div className="flex flex-row items-center justify-end gap-x-4 py-0 pl-0 pr-2">
			<Link to={getMetadataProfileURL(row.original)} target="_blank">
				<OpenInNewIcon className="h-4 w-4" aria-hidden="true" />
			</Link>
		</div>
	)
}

export default function DataTable({
	data,
	loading,
	showFilter = true,
	showButton = true,
	sortRow = 'date',
	showStats = false,
	walletId = null,
	entryTitle = 'NFT',
}) {
	const tableContainerRef = useRef(null)

	const columns = [
		{
			Header: 'Collection',
			id: 'name',
			Cell: CollectionRow,
		},
		{
			Header: 'Release Date',
			id: 'date',
			accessor: row =>
				row.properties.date ? (
					<TypographyParagraph additionalClasses="text-textPrimaryLight dark:text-textPrimaryDark">
						{new Date(row.properties.date).toLocaleDateString()}
					</TypographyParagraph>
				) : (
					''
				),
		},
		{
			Header: 'External Links',
			id: 'externalLinks',
			Cell: LinksRow,
		},
		// UNCOMMENT AND ADJUST THIS WHEN WE HAVE A WAY TO GET THE COLLECTION SIZE FROM THE API
		{
			Header: 'Collection Size',
			id: 'collectionSize',
			accessor: row =>
				(row.collectionEntries?.length
					? row.collectionEntries.reduce((sum: number, entry: any) => sum + (entry.tokenCount || 0), 0)
					: row.properties.nfts && row.properties.nfts.length
						? row.properties.nfts.reduce((sum: number, entry: any) => {
								if (entry.tokenIDs) return sum + entry.tokenIDs.length
								if (entry.tokenStart && entry.tokenEnd)
									return sum + (entry.tokenEnd - entry.tokenStart) + 1
								return sum
							}, 0)
						: 0
				),
		},
		{
			Header: 'Blockchain',
			id: 'blockchain',
			accessor: row =>
				row.properties.nfts
					? Array.from(
							new Set(
								row.properties.nfts
									.map(nft => formatChainName(nft.chain))
									.filter(name => name !== undefined && name.trim().length),
							),
						).join(', ')
					: '',
		},
		{
			Header: 'Contract',
			id: 'contract',
			accessor: row =>
				row.properties.nfts &&
				row.properties.nfts.length === 1 &&
				row.properties.nfts[0].contract && (
					<a
						href={`${getContractAddressExplorerUrl(row.properties.nfts[0].chain)}/${row.properties.nfts[0].contract}`}
						target="_blank"
					>
						{truncateStringInMiddle(row.properties.nfts[0].contract)}
					</a>
				),
		},
		{
			Header: 'Proof',
			id: 'signatures',
			Cell: SignatureRow,
		},
	]

	//  // Get state and functions for building table
	const tableData = useMemo(() => data, [data])

	const {
		getTableProps,
		getTableBodyProps,
		headerGroups,
		footerGroups,
		rows,
		prepareRow,
	} = useTable({
		columns,
		data: tableData,
		autoResetHiddenColumns: false,
	})

	return (
		<div className="overflow-x-auto">
			<table {...getTableProps()} className="min-w-full table-auto">
				<thead className="py-6">
					{headerGroups.map((headerGroup, index) => (
						<tr
							key={index}
							className="border-b border-uiTertiaryLight font-abc text-paragraphSmall leading-6 text-textSecondaryLight dark:border-uiTertiaryDark dark:text-textSecondaryDark"
						>
							{headerGroup.headers.map((column, index) => {
								return (
									<th
										{...column.getHeaderProps()}
										key={index}
										scope="col"
										className={clsx(
											column.isSorted && 'underline',
											column.Header === 'Collection' &&
												'ml-2 inline min-w-[268px]',
											column.Header === 'Release Date' &&
												'hidden min-w-[100px] md:table-cell',
											column.Header === 'Platform' &&
												'min-w-[120px] md:table-cell',
											column.Header === 'External Links' &&
												'hidden min-w-[20px] md:table-cell',
											column.Header === 'Edition Size' &&
												'min-w-[70px] md:table-cell',
											column.Header === 'Blockchain' &&
												'min-w-[130px] md:table-cell',
											column.Header === 'Contract' &&
												'min-w-[55px] md:table-cell',
											column.Header === 'Proof' && 'min-w-[55px] md:table-cell',
											'relative h-10 py-0 pl-0 pr-0 text-center align-text-bottom font-normal sm:pl-0',
										)}
									>
										{column.render('Header')}
										<span className="absolute top-[5px]">
											{column.isSorted ? (
												column.isSortedDesc ? (
													<ArrowDownIcon className="ml-[2px] h-3 w-3" />
												) : (
													<ArrowUpIcon className="ml-[2px] h-3 w-3" />
												)
											) : (
												''
											)}
										</span>
									</th>
								)
							})}
						</tr>
					))}
				</thead>
				<tbody {...getTableBodyProps()}>
					{rows.map((row, index) => {
						prepareRow(row)
						return (
							<tr
								key={index}
								className={clsx(
									'h-[72px] border-b border-dotted border-grey500 text-sm leading-6 text-textPrimaryLight hover:bg-grey100 dark:text-textPrimaryDark dark:hover:bg-[#161616]',
								)}
							>
								{row.cells.map((cell, index) => {
									return (
										<td
											key={index}
											className={clsx(
												cell.column.Header === 'Collection' &&
													'max-w-[400px] md:table-cell',
												cell.column.Header === 'Release Date' &&
													'hidden md:table-cell',
												cell.column.Header === 'Platform' && 'md:table-cell',
												cell.column.Header === 'External Links' &&
													'hidden md:table-cell',
												cell.column.Header === 'Edition Size' &&
													'md:table-cell',
												cell.column.Header === 'Blockchain' && 'md:table-cell',
												cell.column.Header === 'Contract' && 'md:table-cell',
												cell.column.Header === 'Proof' && 'md:table-cell',
												'whitespace-no-wrap px-0 py-0 text-center',
											)}
										>
											{cell.render('Cell')}
										</td>
									)
								})}
							</tr>
						)
					})}
				</tbody>
			</table>
		</div>
	)
}
